import { useCallback } from "react";
import { pdf } from "@react-pdf/renderer";
import { createElement, ReactElement } from "react";
import { useUserDetails } from "../../services/api-client/useUserDetails";
import { useSnackbar } from "../../services/api-client/useSnackbar";
import { stripMarkdown } from "../../Utilities/StripMarkdown";
import { buildFilename } from "./coverLetterPdfUtils";
import CoverLetterDocument from "./CoverLetterDocument";

interface UseCoverLetterDownloadArgs {
	coverLetterMarkdown: string | undefined;
	companyName: string | undefined;
}

export function useCoverLetterDownload({
	coverLetterMarkdown,
	companyName
}: UseCoverLetterDownloadArgs) {
	const { userDetails } = useUserDetails();
	const { showSnack } = useSnackbar();

	const download = useCallback(async () => {
		const name = userDetails?.name;
		const phone = userDetails?.phone;
		const email = userDetails?.email;
		const city = userDetails?.city;
		const state = userDetails?.state;

		if (!name || (!phone && !email)) {
			console.log("Cover letter download blocked — missing profile fields:", {
				name: name ?? "MISSING",
				phone: phone ?? "MISSING",
				email: email ?? "MISSING",
				city: city ?? "MISSING",
				state: state ?? "MISSING"
			});
			showSnack("NotifyResumeRequired", "dark");
			return;
		}

		if (!coverLetterMarkdown || !companyName) return;

		const plainText = stripMarkdown(coverLetterMarkdown);

		const doc = createElement(CoverLetterDocument, {
			fullName: name,
			email: email ?? "",
			phone: phone ?? "",
			city: city ?? "",
			state: state ?? "",
			coverLetterText: plainText,
			letterDate: new Date()
		});

		const blob = await pdf(doc as unknown as ReactElement).toBlob();
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = buildFilename(companyName);
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}, [userDetails, coverLetterMarkdown, companyName, showSnack]);

	return { download };
}
